// @ts-check
import { AlertTriangle, RefreshCw, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BlockCard from '../components/dashboard/BlockCard';
import { BLOCK_COMPONENTS, BLOCK_ORDER, BLOCK_TITLES } from '../components/dashboard/blocks';
import RemindersPanel from '../components/wholesale/RemindersPanel';
import { useToast } from '../context/ToastContext';
import useAdminAlerts from '../hooks/useAdminAlerts';
import useDashboardSummary from '../hooks/useDashboardSummary';
import adminAuthService from '../services/adminAuthService';
import adminWholesaleService from '../services/adminWholesaleService';

/** Local HH:MM, 24-hour. */
const formatUpdatedAt = (iso) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const DashboardPage = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { alerts, dismiss } = useAdminAlerts();
  const {
    blocks,
    failed,
    loading: blocksLoading,
    error: blocksError,
    range,
    setRange,
    refresh,
    generatedAt,
  } = useDashboardSummary();
  const [wholesaleDue, setWholesaleDue] = useState([]);
  const isSuperAdmin = adminAuthService.getCurrentUser()?.role === 'super_admin';

  useEffect(() => {
    if (blocksError) toast.error(blocksError.message || 'Failed to load dashboard blocks');
  }, [blocksError, toast]);

  const loadWholesaleDue = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      setWholesaleDue(await adminWholesaleService.listReminders());
    } catch (error) {
      // Non-critical: the dashboard works without the wholesale card.
      console.error('Failed to load wholesale follow-ups:', error);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    loadWholesaleDue();
  }, [loadWholesaleDue]);

  // Very first load: nothing to show yet, so a skeleton instead of an empty grid. Later loads keep the old blocks, dimmed.
  const firstLoad = blocksLoading && Object.keys(blocks).length === 0 && failed.length === 0;

  const dismissAlert = async (alertId) => {
    try {
      await dismiss(alertId);
    } catch (error) {
      toast.error('Failed to dismiss alert');
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-serif mb-8">Dashboard</h1>

      {/* Admin Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-3 mb-8">
          {alerts.map((alert) => (
            <div
              key={alert.id}
              className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/50 border border-amber-300 dark:border-amber-800 rounded-lg"
            >
              <AlertTriangle size={20} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">{alert.title}</p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-0.5">{alert.message}</p>
                {alert.alert_type === 'unconfigured_country' && (
                  <button
                    onClick={() => navigate('/settings/pricing')}
                    className="mt-2 text-xs text-amber-800 dark:text-amber-300 underline hover:text-amber-900 dark:hover:text-amber-300"
                  >
                    Configure Pricing
                  </button>
                )}
              </div>
              <button
                onClick={() => dismissAlert(alert.id)}
                className="p-1 text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 flex-shrink-0"
                title="Dismiss"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {isSuperAdmin && <RemindersPanel reminders={wholesaleDue} onChanged={loadWholesaleDue} compact />}

      <div className="flex items-center justify-between mb-6">
        <div className="inline-flex rounded-lg border border-border overflow-hidden">
          {['7d', '30d'].map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              aria-pressed={range === r}
              className={`px-3 py-1.5 text-sm ${range === r ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-muted'}`}
            >
              {r === '7d' ? '7 days' : '30 days'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {blocksLoading && !firstLoad ? (
            <span className="text-xs text-muted-foreground" role="status">
              Updating…
            </span>
          ) : (
            generatedAt && <span className="text-xs text-muted-foreground">Updated {formatUpdatedAt(generatedAt)}</span>
          )}
          <button
            onClick={refresh}
            disabled={blocksLoading}
            className="p-2 border border-border rounded-lg hover:bg-muted"
            title="Refresh"
          >
            <RefreshCw size={16} className={blocksLoading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {blocksError && (
        <div className="flex items-center justify-between gap-3 p-4 mb-6 border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/50 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-300">Couldn't load dashboard blocks.</p>
          <button onClick={refresh} className="px-3 py-1.5 text-sm border border-border rounded-lg bg-card hover:bg-muted">
            Retry
          </button>
        </div>
      )}

      {firstLoad ? (
        <div data-testid="dashboard-skeleton" aria-busy="true" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-40 rounded-lg border border-border bg-muted animate-pulse" />
          ))}
        </div>
      ) : (
        <div
          data-testid="dashboard-grid"
          aria-busy={blocksLoading}
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 transition-opacity ${blocksLoading ? 'opacity-60' : ''}`}
        >
          {BLOCK_ORDER.map((key) => {
            const title = BLOCK_TITLES[key] || key;
            // Failed (listed by the server) shows "Couldn't load"; a null block that did not fail is hidden for this role.
            if (failed.includes(key)) return <BlockCard key={key} title={title} href="#" state="error" />;
            const block = blocks[key];
            const Component = BLOCK_COMPONENTS[key];
            if (!block || !Component) return null;
            if (block.untracked) return <BlockCard key={key} title={title} href={block.href} state="untracked" />;
            return <Component key={key} block={block} />;
          })}
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
